import React, { useState } from 'react';
import { Field, Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { useHistory } from 'react-router-dom';
import { MiniAppFocusWrapper } from '../miniapp-focus-wrapper/miniapp-focus-wrapper';
import { InputField } from 'src/features/common';
import { changePassword, setOtpForSession } from 'src/services/user.service';
import TealiumEvents from 'src/helpers/tealium-events';
import { paymentsDashboardRoute } from 'src/routing/routes';

interface IChangePasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

function ChangePasswordForm() {
  const history = useHistory();
  const [showPasswordMatchError, setShowPasswordMatchError] = useState<boolean>(false);
  const [passwordDetailsValid, setPasswordDetailsValid] = useState<boolean>(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState<boolean>(false);
  const [passwordChangeFail, setPasswordChangeFail] = useState<string>('');

  const validate = (values: IChangePasswordForm) => {
    const errors: FormikErrors<IChangePasswordForm> = {};
    if (!values.oldPassword) {
      errors.oldPassword = 'Old password is required';
    }
    if (!values.newPassword) {
      errors.newPassword = 'New password is required';
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = 'Confirm password is required';
    }
    let allValidatorsPassed = true;
    passwordValidators.forEach((validator) => {
      if (!validator.validate(values.newPassword)) {
        allValidatorsPassed = false;
        return;
      }
    });
    if (values.newPassword && !allValidatorsPassed) {
      errors.newPassword = 'Password does not meet minimum requirements';
    }

    setShowPasswordMatchError(values.newPassword !== values.confirmPassword);
    setPasswordDetailsValid(Object.values(errors).length === 0);
    return errors;
  };

  const submit = async (
    values: IChangePasswordForm,
    helpers: FormikHelpers<IChangePasswordForm>,
  ) => {
    const { setSubmitting } = helpers;
    const abortController = new AbortController();
    const result = await changePassword(values, abortController, false);
    if (!abortController.signal.aborted) {
      setSubmitting(false);
      if (!result.ok && result.message) {
        TealiumEvents.onHandledErrorEventSelfService(result.message);
        setPasswordChangeFail(result.message);
        return;
      }
      setOtpForSession(undefined);
      TealiumEvents.merchantProfileChangePasswordSuccess();

      setPasswordChangeSuccess(true);

      setTimeout(() => {
        setPasswordChangeSuccess(false);
      }, 1500);
      TealiumEvents.merchantProfileChangePasswordSuccess();
      history.push(paymentsDashboardRoute);
    }
  };
  return (
    <Formik<IChangePasswordForm>
      initialValues={{ oldPassword: '', newPassword: '', confirmPassword: '' }}
      validate={validate}
      onSubmit={submit}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ values }) => (
        <Form className="flex flex-col gap-y-4 w-full max-w-[500px]">
          <Field
            name="oldPassword"
            type="password"
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            className="dark"
            fieldType="standard"
            placeholder="Old Password"
            isDark
          />
          <Field
            name="newPassword"
            type="password"
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            className="dark"
            fieldType="standard"
            placeholder="New Password"
            isDark
          />
          <Field
            name="confirmPassword"
            type="password"
            component={MiniAppFocusWrapper}
            innerComponent={InputField}
            className="dark"
            fieldType="standard"
            placeholder="Confirm Password"
            isDark
          />
          <p className="text-grey-400 pl-[14px]">
            Must contain:{' '}
            {passwordValidators.map((validator, index) => (
              <span
                key={index}
                className={`${validator.validate(values.newPassword) ? 'text-white-50' : ''}`}
              >
                {validator.description}{' '}
              </span>
            ))}
          </p>
          {showPasswordMatchError && values.confirmPassword.length > 0 && (
            <div className="flex justify-center w-full p-4">
              <p className="text-lg text-red-600">Passwords do not match</p>
            </div>
          )}
          {passwordChangeSuccess && (
            <div className="flex justify-center w-full p-4">
              <p className="text-lg text-blue-600">Passwords do not match</p>
            </div>
          )}
          {passwordChangeFail && (
            <div className="flex justify-center w-full p-4">
              <p className="text-lg text-red-600">{passwordChangeFail}</p>
            </div>
          )}
          <div className="flex w-full gap-x-2">
            <button
              type="reset"
              className="w-full h-12 p-2 border-2 border-white border-solid max-w-26 rounded-40 disabled:bg-grey-700 disabled:border-none disabled:cursor-not-allowed"
              onClick={() => TealiumEvents.merchantProfileChangePasswordClickCancel()}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full h-12 bg-red-500 border-2 max-w-26 border-red-500p-2 rounded-40 disabled:bg-grey-700 disabled:border-none disabled:cursor-not-allowed"
              disabled={!passwordDetailsValid || showPasswordMatchError}
              onClick={() => TealiumEvents.merchantProfileClickChangePassword()}
            >
              Save
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

const passwordValidators = [
  {
    description: '1 number, ',
    validate: (value) => {
      return /\d/.test(value);
    },
  },
  {
    description: '1 upper &',
    validate: (value) => {
      return /[A-Z]/.test(value);
    },
  },
  {
    description: 'lowercase character, ',
    validate: (value) => {
      return /[a-z]/.test(value);
    },
  },
  {
    description: '1 special character, ',
    validate: (value) => {
      return /[!"#\$%&'\(\)\*\+,-\.\/:;<=>\?\@\[\\\]\^_`{\|}~]/.test(value);
    },
  },
  {
    description: '8 characters minimum',
    validate: (value) => {
      return value && value.length >= 8;
    },
  },
];

export default ChangePasswordForm;
